import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { TrackingEvent } from '@/tracking/tracking-manager';
import { ROUTES } from '@/utils/constants';

type TrackingData = {
    module: string | null;
    gameUid: string | null;
    gameName: string | null;
    gamePosition: string | null;
    zoneName: string | null;
    zoneLayout: string | null;
    zonePosition: string | null;
    gameCollectionName?: string;
};

type EventConfig = {
    event: 'Game Video Auto Played Successful' | 'Game Video Muted' | 'Game Video Unmuted' | 'Game Video 100pc Played';
    getModule: (data: TrackingData) => string;
    getEventLabel: (data: TrackingData) => string;
    getGameID: (data: TrackingData) => string;
    getGameName: (data: TrackingData) => string;
    getGamePosition: (data: TrackingData) => string;
    getZoneName: (data: TrackingData) => string;
    getZoneLayout: (data: TrackingData) => string;
    getZonePosition: (data: TrackingData) => string;
};

const EVENT_CONFIGS: Record<string, EventConfig> = {
    gameVideoAutoPlayed: {
        event: 'Game Video Auto Played Successful',
        getModule: (data) => toSnakeCase(data.module || ''),
        getEventLabel: (data) => toSnakeCase(data.gameName || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGamePosition: (data) => data.gamePosition || '',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getZonePosition: (data) => data.zonePosition || ''
    },
    gameVideoMuted: {
        event: 'Game Video Muted',
        getModule: (data) => toSnakeCase(data.module || ''),
        getEventLabel: (data) => toSnakeCase(data.gameName || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGamePosition: (data) => data.gamePosition || '',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getZonePosition: (data) => data.zonePosition || ''
    },
    gameVideoUnmuted: {
        event: 'Game Video Unmuted',
        getModule: (data) => toSnakeCase(data.module || ''),
        getEventLabel: (data) => toSnakeCase(data.gameName || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGamePosition: (data) => data.gamePosition || '',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getZonePosition: (data) => data.zonePosition || ''
    },
    gameVideo100pcViewed: {
        event: 'Game Video 100pc Played',
        getModule: (data) => toSnakeCase(data.module || ''),
        getEventLabel: (data) => toSnakeCase(data.gameName || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGamePosition: (data) => data.gamePosition || '',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getZonePosition: (data) => data.zonePosition || ''
    }
};

const buildEventProps = getEventPropsBuilder(
    (config: EventConfig, data: TrackingData | null): Partial<AmplitudeEventKeys> => {
        if (!data) {
            return {
                event: config.event
            };
        }
        const eventProps: Partial<AmplitudeEventKeys> = {
            event: config.event,
            Module: config.getModule(data),
            'Event Label': config.getEventLabel(data),
            'Game ID': config.getGameID(data),
            'Game Name': config.getGameName(data),
            Position: config.getGamePosition(data),
            'Zone Name': config.getZoneName(data),
            'Zone Position': config.getZonePosition(data)
        };
        const zoneLayout = config.getZoneLayout(data);
        if (zoneLayout) {
            eventProps['Zone Layout'] = zoneLayout;
        }
        if (data.gameCollectionName) {
            eventProps['Game Collection Name'] = toSnakeCase(data.gameCollectionName);
        }
        return eventProps;
    }
);

const extractTrackingData = (element: HTMLElement | null): TrackingData | null => {
    if (!element) {
        return null;
    }

    const game = element.closest('[data-t-gameuid]');
    const gameUid = game?.getAttribute('data-t-gameuid');
    const gamePosition = game?.getAttribute('data-t-gameposition');
    let gameName = game?.getAttribute('data-t-gamename');
    if (!gameName) {
        gameName = element.closest('[data-t-gamename]')?.getAttribute('data-t-gamename');
    }

    const zone = element.closest('[data-t-zoneuid]');
    const zonePosition = zone?.getAttribute('data-t-zoneposition');
    const zoneNameFromZone = zone?.getAttribute('data-t-zonename');
    const zoneLayoutFromZone = zone?.getAttribute('data-t-zonelayout');

    const exclusiveLaunch = element.closest('[data-t-prepostlaunch]');
    const exclusiveLaunchType = exclusiveLaunch?.getAttribute('data-t-prepostlaunch');
    const exclusiveLaunchZoneName = exclusiveLaunch?.getAttribute('data-t-zonename');
    const exclusiveLaunchZoneLayout = exclusiveLaunch?.getAttribute('data-t-zonelayout');

    const exclusiveLaunchModal = element.closest('[data-t-prepostlaunchmodal]');
    const exclusiveLaunchTypeModal = exclusiveLaunchModal?.getAttribute('data-t-prepostlaunchmodal');
    const exclusiveLaunchModalZoneName = exclusiveLaunchModal?.getAttribute('data-t-zonename');
    const exclusiveLaunchModalZoneLayout = exclusiveLaunchModal?.getAttribute('data-t-zonelayout');

    let zoneModule;
    const { pathname } = window.location;
    const isCategoryPage = pathname.startsWith('/c/');

    if (isCategoryPage) {
        zoneModule = 'explore_trailer';
    } else if (exclusiveLaunchType) {
        zoneModule = exclusiveLaunchType;
    } else if (exclusiveLaunchTypeModal) {
        zoneModule = `${exclusiveLaunchTypeModal}_modal`;
    } else {
        zoneModule = 'game_zone';
    }

    const zoneName =
        zoneNameFromZone ||
        exclusiveLaunchZoneName ||
        exclusiveLaunchModalZoneName ||
        exclusiveLaunchType ||
        exclusiveLaunchTypeModal ||
        zoneModule ||
        null;

    const zoneLayout = zoneLayoutFromZone || exclusiveLaunchZoneLayout || exclusiveLaunchModalZoneLayout || null;

    const gameCollectionName = pathname.startsWith(ROUTES.CATEGORY_HUB)
        ? pathname.slice(ROUTES.CATEGORY_HUB.length)
        : undefined;

    return {
        module: zoneModule,
        gameUid: gameUid || null,
        gameName: gameName || null,
        gamePosition: gamePosition || null,
        zoneName,
        zoneLayout,
        zonePosition: zonePosition || null,
        gameCollectionName
    };
};

export const trackGameVideoEvent = (
    eventType: 'gameVideoAutoPlayed' | 'gameVideoMuted' | 'gameVideoUnmuted' | 'gameVideo100pcViewed',
    source?: { current: HTMLElement | null } | null
): { event: TrackingEvent; trackingData: TrackingData | null } => {
    const config = EVENT_CONFIGS[eventType];
    if (!config) {
        console.warn(`[Tracking] Unknown game video event type: ${eventType}`);
    }

    const trackingData = source?.current ? extractTrackingData(source.current) : null;
    const eventProps = buildEventProps(config, trackingData);

    return {
        event: {
            destination: 'amplitude',
            eventProps
        },
        trackingData
    };
};
